import {
	BusyIndicator,
	Button,
	ChangeTriggerKind,
	ContentWithSidebar,
	Dialog,
	Form,
	FormItem,
	InputRow,
	ItemSelectorLayout,
	ListItem,
	NoResults,
	SearchInputForm,
	TableView,
	TextInput,
	Toolbar,
	TreeAccordion,
	ScrollableVirtualList,
	SidebarHandle,
	TableViewNoResultConfig
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./SelectRecipientsDialog.module.less";
import {
	generateTrackingId,
	isNotNilOrEmpty,
	isNotNilOrWhiteSpace
} from "client/Misc/utils";
import {
	ItemSelectionKind,
	LazyLoadCursorKind,
	MediaQueryKind,
	RemoteDataSourceState,
	SelectableItemOptions,
	createRemoteDataSource,
	useDataInputDialog,
	useSelection,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	ContactListItem,
	ContactSource,
	ContactsSearchResult,
	DisplayContactModel,
	findPersonalContacts,
	listGlobalContacts
} from "client/Api/Email/contacts";
import { never } from "client_legacy/ndaw/errors";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { InputValidationResultKind, createValidationConfiguration } from "client/Validation";
import { isNotNullNorUndefined } from "client/Validation/rules";
import { isValidRecipientValue } from "../validation";
import { EMAIL_SEPARATOR } from "./RecipientsAutocomplete";

const PAGE_SIZE = 100;

export type SelectRecipientsDialogProps = {
	initialRecipients: Recipients;
	onSelected: (recipients: Recipients) => void;
	onClose: () => void;
};

export const SelectRecipientsDialog = (props: SelectRecipientsDialogProps): ReactElement => {
	const {
		initialRecipients,
		onSelected,
		onClose
	} = props;

	const [formData, setFormData] = useState<Recipients>(initialRecipients);
	const [activeSource, setActiveSource] = useState<RecipientsContactSource>(ContactSource.Personal);
	const [selectedItems, setSelectedItems] = useState<readonly ContactItem[]>([]);
	const sidebarRef: RefObject<SidebarHandle | null> = useRef<SidebarHandle>(null);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const formHandler: IFormHandler<Recipients> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const changeSourceHandler = useCallback(
		(source: RecipientsContactSource): void => {
			if (isMobile) {
				sidebarRef.current
					?.onClose();
			}

			setSelectedItems([]);
			setActiveSource(source);
		},
		[isMobile]
	);

	const addRecipientsTo = useCallback(
		(kind: RecipientKind): void => {
			if (selectedItems.length === 0) {
				return;
			}

			const patchRecipients = (input: string): string => {
				let result: string = input.trim();

				if (result) {
					const endsWithSeparator: boolean = result.slice(-1) === EMAIL_SEPARATOR;
					if (endsWithSeparator) {
						result += " ";
					} else {
						result += `${EMAIL_SEPARATOR} `;
					}
				}

				result += selectedItems.map(x => x.emailAddress)
					.join(`${EMAIL_SEPARATOR} `);

				return result;
			};

			setFormData(
				(x): Recipients => {
					return {
						...x,
						[kindToPropertyMap[kind]]: patchRecipients(x[kindToPropertyMap[kind]])
					};
				}
			);
		},
		[selectedItems]
	);

	const doneHandler = useCallback(
		async (): Promise<void> => {
			try {
				const validationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					return;
				}

				onSelected(formData);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
			}
		},
		[
			formData,
			formHandler.validation,
			globalCancellation,
			onSelected
		]
	);

	return (
		<Dialog
			trackingId="select-recipients-dialog"
			variant="left-slider"
			onClose={onClose}
		>
			<Dialog.Header
				title={localize("_SelectContacts")}
				onClose={onClose}
				hasBorder
			/>

			<Dialog.Body
				goesEdgeToEdge
			>
				<ContentWithSidebar>
					<ContentWithSidebar.Sidebar ref={sidebarRef}>
						<TreeAccordion trackingId="contact-source-selector">
							{sourceOptions.map((x): ReactElement => (
								<TreeAccordion.Node key={x.value}>
									<TreeAccordion.Item
										isSelected={activeSource === x.value}
										onClick={() => changeSourceHandler(x.value)}
										trackingId={generateTrackingId("contact-source", `|${x.value}`)}
										title={localize(x.key)}
									>
										{localize(x.key)}
									</TreeAccordion.Item>
								</TreeAccordion.Node>
							))}
						</TreeAccordion>
					</ContentWithSidebar.Sidebar>
					<ContentWithSidebar.Content hasNoPadding>
						<Content
							source={activeSource}
							isMobile={isMobile}
							onSelectedItemsChange={setSelectedItems}
							selectedItems={selectedItems}
						/>
					</ContentWithSidebar.Content>
				</ContentWithSidebar>
			</Dialog.Body>

			<Dialog.Footer hasBorder>
				<Form onSubmit={doneHandler}>
					<div className={styles.formLayout}>
						<FormItem>
							<InputRow>
								<Button
									kind="secondary"
									trackingId="add-to"
									text={localize("_To")}
									onClick={() => addRecipientsTo(RecipientKind.To)}
								/>

								<InputRow.Stretch>
									<TextInput {...formHandler.for("to").attachToTextInput()} />
								</InputRow.Stretch>
							</InputRow>
						</FormItem>

						<FormItem>
							<InputRow>
								<Button
									kind="secondary"
									trackingId="add-cc"
									text={localize("_Cc")}
									onClick={() => addRecipientsTo(RecipientKind.Cc)}
								/>

								<InputRow.Stretch>
									<TextInput {...formHandler.for("cc").attachToTextInput()} />
								</InputRow.Stretch>
							</InputRow>
						</FormItem>

						<FormItem>
							<InputRow>
								<Button
									kind="secondary"
									trackingId="add-bcc"
									text={localize("_Bcc")}
									onClick={() => addRecipientsTo(RecipientKind.Bcc)}
								/>

								<InputRow.Stretch>
									<TextInput {...formHandler.for("bcc").attachToTextInput()} />
								</InputRow.Stretch>
							</InputRow>
						</FormItem>
					</div>
				</Form>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Done")}
						trackingId="done"
						onClick={doneHandler}
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						trackingId="cancel"
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
SelectRecipientsDialog.displayName = "SelectRecipientsDialog";

enum RecipientKind {
	To = 1,
	Cc = 2,
	Bcc = 3
}

type RecipientsContactSource = Exclude<ContactSource, ContactSource.Workspace | ContactSource.Suggested>;

const kindToPropertyMap: { [key in RecipientKind]: keyof Recipients; } = {
	[RecipientKind.To]: "to",
	[RecipientKind.Cc]: "cc",
	[RecipientKind.Bcc]: "bcc"
};

type SourceOption = {
	value: RecipientsContactSource;
	key: LocalizationKey;
};

const sourceOptions: readonly SourceOption[] = [
	{ value: ContactSource.Personal, key: "_Personal(Contacts)" },
	{ value: ContactSource.Gal, key: "_GlobalAddressList" }
];

const validationConfiguration = createValidationConfiguration<Recipients>();

validationConfiguration.forScalar("to")
	.add(
		isNotNullNorUndefined(),
		isValidRecipientValue
	);

validationConfiguration.forScalar("cc")
	.add(
		isNotNullNorUndefined(),
		isValidRecipientValue
	);

validationConfiguration.forScalar("bcc")
	.add(
		isNotNullNorUndefined(),
		isValidRecipientValue
	);

type ContentProps = {
	source: RecipientsContactSource;
	selectedItems: readonly ContactItem[];
	onSelectedItemsChange: (value: readonly ContactItem[]) => void;
	isMobile: boolean;
};

const Content = (props: ContentProps): ReactElement => {
	const {
		source,
		isMobile,
		selectedItems,
		onSelectedItemsChange
	} = props;

	switch (source) {
		case ContactSource.Personal:
			return (
				<PersonalContacts
					isMobile={isMobile}
					onSelectedItemsChange={onSelectedItemsChange}
					selectedItems={selectedItems}
				/>
			);
		case ContactSource.Gal:
			return (
				<GalContacts
					isMobile={isMobile}
					onSelectedItemsChange={onSelectedItemsChange}
					selectedItems={selectedItems}
				/>
			);
		default:
			never(source);
	}
};
Content.displayName = "Content";

type PersonalContactsProps = {
	selectedItems: readonly ContactItem[];
	onSelectedItemsChange: (value: readonly ContactItem[]) => void;
	isMobile: boolean;
};

const PersonalContacts = (props: PersonalContactsProps): ReactElement => {
	const {
		isMobile,
		selectedItems,
		onSelectedItemsChange
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		version: newGuid(),
		value: ""
	});

	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);

	const searchHandler = useCallback(
		(value: string, version: Guid): void => {
			setDesiredAmountOfItems(PAGE_SIZE);

			setSearchTerm({
				value,
				version
			});
		},
		[]
	);

	const requestMoreItemsHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems(x => x + PAGE_SIZE);
		},
		[]
	);

	const {
		currentItems,
		hasMoreOnServer,
		getLoadingState,
		reload
	} = usePersonalContactsDataSource({
		searchTerm: searchTerm.value,
		desiredAmountOfItems
	});

	return (
		<Contacts
			currentItems={currentItems}
			searchTerm={searchTerm}
			hasMoreOnServer={hasMoreOnServer}
			source={ContactSource.Personal}
			selectedItems={selectedItems}
			isMobile={isMobile}
			onSearchTermChange={searchHandler}
			loadingState={getLoadingState()}
			onReloadList={reload}
			onRequestMoreItems={requestMoreItemsHandler}
			onSelectedItemsChange={onSelectedItemsChange}
		/>
	);
};
PersonalContacts.displayName = "PersonalContacts";

type GalContactsProps = {
	selectedItems: readonly ContactItem[];
	onSelectedItemsChange: (value: readonly ContactItem[]) => void;
	isMobile: boolean;
};

const GalContacts = (props: GalContactsProps): ReactElement => {
	const {
		isMobile,
		selectedItems,
		onSelectedItemsChange
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		version: newGuid(),
		value: ""
	});

	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);

	const searchHandler = useCallback(
		(value: string, version: Guid): void => {
			setDesiredAmountOfItems(PAGE_SIZE);

			setSearchTerm({
				value,
				version
			});
		},
		[]
	);

	const requestMoreItemsHandler = useCallback(
		(): void => {
			setDesiredAmountOfItems(x => x + PAGE_SIZE);
		},
		[]
	);

	const {
		currentItems,
		hasMoreOnServer,
		getLoadingState,
		reload
	} = useGalContactsDataSource({
		searchTerm: searchTerm.value,
		desiredAmountOfItems
	});

	return (
		<Contacts
			currentItems={currentItems}
			searchTerm={searchTerm}
			hasMoreOnServer={hasMoreOnServer}
			source={ContactSource.Gal}
			selectedItems={selectedItems}
			isMobile={isMobile}
			onSearchTermChange={searchHandler}
			loadingState={getLoadingState()}
			onReloadList={reload}
			onRequestMoreItems={requestMoreItemsHandler}
			onSelectedItemsChange={onSelectedItemsChange}
		/>
	);
};
GalContacts.displayName = "GalContacts";

type ContactsProps = {
	source: RecipientsContactSource;
	searchTerm: VersionedValue<string>;
	currentItems: readonly ContactItem[];
	selectedItems: readonly ContactItem[];
	loadingState: RemoteDataSourceState;
	isMobile: boolean;
	hasMoreOnServer: boolean;
	onSearchTermChange: (value: string, version: Guid) => void;
	onSelectedItemsChange: (value: readonly ContactItem[]) => void;
	onReloadList: () => void;
	onRequestMoreItems: () => void;
};

const Contacts = (props: ContactsProps): ReactElement => {
	const {
		source,
		searchTerm,
		currentItems,
		selectedItems,
		loadingState,
		isMobile,
		hasMoreOnServer,
		onSearchTermChange,
		onSelectedItemsChange,
		onReloadList,
		onRequestMoreItems
	} = props;

	const {
		multiSelection,
		selectionFor
	} = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems: onSelectedItemsChange,
			hasMemorization: true
		}
	);

	const tableNoResultConfig = useMemo(
		(): TableViewNoResultConfig | undefined => {
			if (loadingState === "loading") {
				return undefined;
			}

			if (isNotNilOrEmpty(searchTerm.value)) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "address-book",
				title: localize("_NoContactsYet"),
				message: localize("_ThereAreNoContactsYetInThisAddressBook")
			};
		},
		[loadingState, searchTerm.value]
	);

	const renderNoResults = (kind: "error" | "no-items" | "no-search-results"): ReactElement => {
		switch (kind) {
			case "error":
				return (
					<NoResults
						icon="gear"
						title={localize("_NotAbleToRetrieveContacts")}
						message={localize("_WeWereUnableToRetrieveYourContacts")}
						onRetry={onReloadList}
						height="fill"
						trackingId="error-screen"
					/>
				);
			case "no-items":
				return (
					<NoResults
						icon="address-book"
						title={localize("_NoContactsYet")}
						message={localize("_ThereAreNoContactsYetInThisAddressBook")}
						height="fill"
						trackingId="no-contacts"
					/>
				);
			case "no-search-results":
				return (
					<NoResults
						icon="magnifying-glass"
						title={localize("_NoSearchResults")}
						message={localize("_PleaseTrySearchingWithAnotherTerm")}
						height="fill"
						trackingId="no-search-results"
					/>
				);
			default:
				never(kind);
		}
	};

	const renderAsTable = (): ReactElement => {
		return (
			<TableView
				trackingId={generateTrackingId("table-view", `|${source}`)}
				columns={[
					{
						title: localize("_Name"),
						trackingId: "name",
						width: "fill"
					},
					{
						title: localize("_EmailAddress"),
						trackingId: "email",
						width: "fill"
					}
				]}
				items={currentItems}
				height="stretch"
				itemKey="id"
				multiSelectable={multiSelection}
				renderAfterItems={
					<TableView.LoadMoreIndicator
						isLoadingData={loadingState === "loading"}
						hasMoreItems={hasMoreOnServer}
						onRequestMoreItems={onRequestMoreItems}
					/>
				}
				noResultConfig={tableNoResultConfig}
				isVirtual
			>
				{(item): ReactElement => (
					<TableView.Row
						trackingId={item.id}
						selectable={selectionFor(item)}
					>
						<TableView.Cell>
							{item.displayName}
						</TableView.Cell>

						<TableView.Cell>
							{item.emailAddress}
						</TableView.Cell>
					</TableView.Row>
				)}
			</TableView>
		);
	};

	const renderAsList = (): ReactElement => {
		return (
			<ScrollableVirtualList
				items={currentItems}
				itemKey={getUniqueKey}
				estimatedHeightOfAnItem={64}
				renderAfterItems={
					<ScrollableVirtualList.LoadMoreIndicator
						isLoadingData={loadingState === "loading"}
						hasMoreItems={hasMoreOnServer}
						onRequestMoreItems={onRequestMoreItems}
					/>
				}
				trackingId={generateTrackingId("list-view", `|${source}`)}
			>
				{(item: ContactItem): ReactElement => (
					<Item
						item={item}
						selectable={selectionFor(item)}
					/>
				)}
			</ScrollableVirtualList>
		);
	};

	const renderContent = (): ReactElement | null => {
		if (loadingState === "error") {
			return renderNoResults("error");
		}

		if (isMobile) {
			if (currentItems.length === 0) {
				if (loadingState === "loading") {
					return <BusyIndicator />;
				}

				if (isNotNilOrEmpty(searchTerm.value)) {
					return renderNoResults("no-search-results");
				}

				return renderNoResults("no-items");

			}

			return renderAsList();
		}

		return renderAsTable();
	};

	return (
		<ItemSelectorLayout>
			<ItemSelectorLayout.Header>
				<ItemSelectorLayout.Search>
					<SearchInputForm
						value={searchTerm}
						onChange={onSearchTermChange}
						trigger={{
							kind: ChangeTriggerKind.Debounced
						}}
						placeholder={localize("_Search")}
					/>
				</ItemSelectorLayout.Search>
			</ItemSelectorLayout.Header>
			<ItemSelectorLayout.Content>
				{renderContent()}
			</ItemSelectorLayout.Content>
		</ItemSelectorLayout>
	);
};
Contacts.displayName = "Contacts";

type ItemProps = {
	item: ContactItem;
	selectable: SelectableItemOptions;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		item,
		selectable
	} = props;

	const isSelected: boolean = selectable && selectable.isSelected;

	const clickHandler = useCallback(
		(): void => {
			if (!selectable) {
				return;
			}

			selectable.onSelect(ItemSelectionKind.Append);
		},
		[selectable]
	);

	return (
		<ListItem
			isActive={isSelected}
			trackingId={generateTrackingId("list-item", `|${item.id}`)}
		>
			{selectable && (
				<ListItem.Checkbox
					value={isSelected}
					onChange={clickHandler}
				/>
			)}
			<ListItem.Content onClick={clickHandler}>
				<ListItem.Title>
					{isNotNilOrWhiteSpace(item.displayName)
						? item.displayName
						: item.emailAddress
					}
				</ListItem.Title>
				{isNotNilOrWhiteSpace(item.displayName) && (
					<ListItem.SubTitle>
						{item.emailAddress}
					</ListItem.SubTitle>
				)}
			</ListItem.Content>
		</ListItem>
	);
};
Item.displayName = "Item";

type ContactItem = {
	id: string;
	displayName: string | null;
	emailAddress: string | null;
};

const usePersonalContactsDataSource = createRemoteDataSource<DisplayContactModel>()
	.setConfiguration({
		hasSearch: true,
		lazyLoadCursor: LazyLoadCursorKind.SkipTake
	})
	.getHook({
		performRequest: async (options): Promise<LazyLoadPageResult<DisplayContactModel>> => {
			const {
				searchTerm,
				skip,
				amount,
				signal
			} = options;

			const originalResult: ContactsSearchResult = await findPersonalContacts(
				searchTerm,
				amount,
				skip,
				signal
			);

			const hasMore: boolean = originalResult.total > skip + amount;

			return {
				hasMore,
				items: originalResult.contacts
			};
		}
	});

const useGalContactsDataSource = createRemoteDataSource<
	ContactListItem
>()
	.setConfiguration({
		hasSearch: true,
		lazyLoadCursor: LazyLoadCursorKind.SkipToken,
	})
	.getHook(
		{
			performRequest: async (options): Promise<SkipTokenPageResult<ContactListItem>> => {
				return await listGlobalContacts(
					options.searchTerm,
					options.amount,
					options.skipToken,
					options.signal
				);
			}
		}
	);

function getUniqueKey(contact: ContactItem): string {
	return contact.id;
}

export type Recipients = {
	to: string;
	cc: string;
	bcc: string;
};

export type SelectRecipientsDialogInitialData = Omit<
	SelectRecipientsDialogProps,
	"onSelected" | "onClose"
>;

export function useSelectRecipientsDialog(): [
	element: ReactElement | null,
	open: (props: SelectRecipientsDialogInitialData) => Promise<TryResult<Recipients>>
] {
	return useDataInputDialog(
		(props, onClose, onSelected) => (
			<SelectRecipientsDialog
				{...props}
				onSelected={onSelected}
				onClose={onClose}
			/>
		)
	);
}
